import { useCSTAction, useOnCSTOpen } from '../../../../chat/cst-state/cst.actions';
import type { PauseOrResumeOrderActionProps } from './PauseOrResumeOrderAction.types';
import { PauseOrderActionReason } from './PauseOrResumeOrderAction.types';
import { getPauseOrderActionOptions } from './utils/getPauseOrderActionOptions';
import type { LabsGqlLabOrderFragment } from '@orthly/graphql-operations';
import type { LabsGqlCreateOrderHoldMutationVariables } from '@orthly/graphql-operations';
import { useCreateOrderHoldMutation } from '@orthly/graphql-react';
import {
    FlossPalette,
    Icon,
    MiniWindowVariant,
    RootActionDialog,
    SimpleSelect,
    styled,
    useChangeSubmissionFn,
} from '@orthly/ui';
import { Box, Button, Divider, Text } from '@orthly/ui-primitives';
import React from 'react';

const ContentBox = styled(Box)({
    display: 'grid',
    gap: '48px',
    marginTop: '24px',
});

const LeftContentBox = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '32px',
});

const RightContentBox = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'end',
    gap: '8px',
});

const SplitOrderContentBox = styled(Box)({
    background: FlossPalette.SECONDARY_BACKGROUND,
    width: '100%',
    position: 'absolute',
    left: 0,
    bottom: 0,
    padding: '8px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'start',
    gap: '8px',
});

const SelectBox = styled(Box)({
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    gap: '8px',
});

const ExplanationBox = styled(Box)({
    background: FlossPalette.TAN,
    border: `1px solid ${FlossPalette.DARK_TAN}`,
    borderRadius: '8px',
    padding: '24px',
    display: 'grid',
    gap: '12px',
});

const ReviewDesignDivider = styled(Divider)({
    borderColor: FlossPalette.DARK_TAN,
    margin: '8px 0 6px',
});

const SplitOrderIcon = styled(Icon)({
    color: FlossPalette.SECONDARY_FOREGROUND,
    fontSize: 12,
});

const SplitOrderBanner: React.VFC = () => {
    return (
        <SplitOrderContentBox data-testid={'split-order-banner'}>
            <SplitOrderIcon icon={'ExclamationIcon'} color={'inherit'} />
            <Text variant={'body2'} medium color={'SECONDARY_FOREGROUND'}>
                This patient has items in a different order. You will have to pause the order separately if you wish to
                do so.
            </Text>
        </SplitOrderContentBox>
    );
};

interface PauseOrderActionInnerProps {
    order: LabsGqlLabOrderFragment;
    selectedOption: string | undefined;
    setSelectedOption: (value: string | undefined) => void;
    onCloseAction: () => void;
    onPauseAction: () => void;
    orderHasSplitOrders: boolean;
}

const PauseOrderActionContent: React.FC<PauseOrderActionInnerProps> = ({
    order,
    selectedOption,
    setSelectedOption,
    onCloseAction,
    onPauseAction,
    orderHasSplitOrders,
}) => {
    const options = getPauseOrderActionOptions(order);

    const setCSTVariant = useCSTAction('SET_WINDOW_VARIANT');
    const openCSTWithoutGroupAction = useOnCSTOpen(() => {
        onCloseAction();
        setCSTVariant(MiniWindowVariant.Maximized, 'move');
    });

    return (
        <ContentBox sx={{ paddingBottom: orderHasSplitOrders ? '24px' : 0 }}>
            <LeftContentBox>
                <SelectBox>
                    <Text variant={'body2'} medium>
                        Why do you want to pause this order?
                    </Text>
                    <SimpleSelect
                        options={options}
                        onChange={v => setSelectedOption(v)}
                        value={selectedOption}
                        label={!!selectedOption ? 'Pause reason' : 'Make a selection'}
                    />
                </SelectBox>

                <ExplanationBox>
                    <Text variant={'body2'} color={'GRAY'}>
                        What happens when I pause my order?
                    </Text>
                    <Text variant={'body2'}>
                        This order will be paused for the next 45 days or until you resume the order.{' '}
                        <strong>After 45 days, the order will be slotted for deletion.</strong>
                    </Text>
                    {selectedOption === PauseOrderActionReason.ReviewDesign && (
                        <>
                            <ReviewDesignDivider orientation={'horizontal'} />

                            <Text variant={'body2'} color={'GRAY'}>
                                How will I be able to review my design?
                            </Text>
                            <Text variant={'body2'}>
                                Your design will be available to review once you resume the order.
                            </Text>
                        </>
                    )}
                </ExplanationBox>
            </LeftContentBox>

            <RightContentBox>
                <Button variant={'ghost'} startIcon={'SidebarChatIcon'} onClick={openCSTWithoutGroupAction}>
                    I want to consult with a technical expert
                </Button>
                <Button variant={'secondary-gray'} onClick={onCloseAction}>
                    Cancel
                </Button>
                <Button
                    data-test={'pause-order-button'}
                    variant={'primary'}
                    onClick={onPauseAction}
                    disabled={!selectedOption}
                >
                    Pause order
                </Button>
            </RightContentBox>
        </ContentBox>
    );
};

type CreateOrderHoldVars = LabsGqlCreateOrderHoldMutationVariables['data'];
type PauseOrderActionProps = PauseOrResumeOrderActionProps;

const useCreateOrderHold = (onSuccess: () => void) => {
    const [submitCreateHoldMtn] = useCreateOrderHoldMutation();
    const createHoldMtnSubmitter = (data: CreateOrderHoldVars) => submitCreateHoldMtn({ variables: { data } });
    return useChangeSubmissionFn<any, [CreateOrderHoldVars]>(createHoldMtnSubmitter, {
        successMessage: () => ['This order will be paused for the next 45 days or until you resume the order.'],
        onSuccess,
    });
};

export const PauseOrderAction: React.VFC<PauseOrderActionProps> = ({
    order,
    refetch,
    leftAlign,
    buttonVariant,
    preselectedOption,
}) => {
    const [open, setOpen] = React.useState(false);
    const [selectedOption, setSelectedOption] = React.useState<string | undefined>(preselectedOption);

    const orderHasSplitOrders = (order.split_order_count ?? 0) > 0;

    const openPauseOrderModal = React.useCallback(() => {
        // TODO: [EPDPOR-2150] implement analytics
        setOpen(true);
    }, [setOpen]);

    const closePauseOrderModal = React.useCallback(() => {
        // TODO: [EPDPOR-2150] implement analytics
        setOpen(false);
        setSelectedOption(undefined);
    }, [setOpen, setSelectedOption]);

    const { submit: submitCreateHold, submitting: submittingCreateHold } = useCreateOrderHold(() => {
        refetch();
        setOpen(false);
    });

    const onPauseAction = () => {
        void submitCreateHold({
            hold_details: {
                hold_category: selectedOption ?? '',
                hold_is_practice_managed_pause: true,
            },
            orderId: order.id,
            show_hold_status_to_practice: true,
        });
    };

    return (
        <RootActionDialog
            showCloseButton
            disableActionsBorder
            loading={submittingCreateHold}
            open={open}
            setOpen={setOpen}
            onClose={closePauseOrderModal}
            maxWidth={'md'}
            title={'Pause this order'}
            content={
                <PauseOrderActionContent
                    order={order}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    orderHasSplitOrders={orderHasSplitOrders}
                    onCloseAction={closePauseOrderModal}
                    onPauseAction={onPauseAction}
                />
            }
            actions={orderHasSplitOrders && <SplitOrderBanner />}
            CustomButton={() => (
                <Button
                    data-test={'open-pause-order-modal-button'}
                    leftAlign={leftAlign}
                    variant={buttonVariant}
                    startIcon={'PauseIcon'}
                    onClick={openPauseOrderModal}
                >
                    Pause order
                </Button>
            )}
        />
    );
};
